import React from 'react'

import { useTicketFiltersContext } from '@/context/filters/ticketFilters/ticketFilters'
import useTicketSection from '@/hooks/useDashboard/useTicketSection'
import { useGuardedNavigate } from '@/hooks/useGuardedNavigate'
import { Status } from '@/types/Ticket'

import { Card } from './Card'
import LoadingSkeleton from './LoadingSkeleton'

const Tickets = () => {
  const ticketQuery = useTicketSection()
  const ticketData = ticketQuery?.data?.agmDashboard
  const { nonOtaTicketTypes } = ticketQuery
  const { setFilters } = useTicketFiltersContext()
  const navigate = useGuardedNavigate()

  // Helper to set non-OTA ticket type filters for navigation handlers
  const setNonOtaFiltersAndNavigate = (statuses: Status[], ticketReasons?: string[]) => {
    setFilters({
      ticketTypes: nonOtaTicketTypes.length > 0 ? [...nonOtaTicketTypes] : undefined,
      ticketReasons: ticketReasons ?? undefined,
      ticketStatuses: statuses,
      startingCreatedAtDate: undefined,
      endingCreatedAtDate: undefined,
    })
    navigate('/tickets')
  }

  const handleOpenTickets = () => {
    setNonOtaFiltersAndNavigate([
      Status.NEW,
      Status.WORKING,
      Status.NEEDS_ACTION,
      Status.AWAITING_RESPONSE,
      Status.ESCALATED,
    ])
  }

  const handleEscalatedTickets = () => {
    setNonOtaFiltersAndNavigate([Status.ESCALATED])
  }

  const handleNewTickets = () => {
    setNonOtaFiltersAndNavigate([Status.NEW])
  }

  const handleNeedsActionTickets = () => {
    setNonOtaFiltersAndNavigate([Status.NEEDS_ACTION])
  }

  const handleAwaitingResponseTickets = () => {
    setNonOtaFiltersAndNavigate([Status.AWAITING_RESPONSE])
  }

  const handleSevenDayGuaranteeTickets = () => {
    setFilters({
      ticketTypes: ['7-Day Guarantee'],
      ticketReasons: undefined,
      ticketStatuses: [Status.NEW, Status.WORKING, Status.NEEDS_ACTION, Status.AWAITING_RESPONSE],
      startingCreatedAtDate: undefined,
      endingCreatedAtDate: undefined,
    })
    navigate('/tickets')
  }

  const handleMaintenanceTickets = () => {
    setFilters({
      ticketTypes: ['Maintenance'],
      ticketReasons: undefined,
      ticketStatuses: [Status.NEW, Status.WORKING, Status.NEEDS_ACTION, Status.AWAITING_RESPONSE],
      startingCreatedAtDate: undefined,
      endingCreatedAtDate: undefined,
    })
    navigate('/tickets')
  }

  const handleLexitTickets = () => {
    setFilters({
      ticketTypes: ['Reservation'],
      ticketReasons: ['Lexit'],
      ticketStatuses: [Status.NEW, Status.WORKING, Status.NEEDS_ACTION, Status.AWAITING_RESPONSE],
      startingCreatedAtDate: undefined,
      endingCreatedAtDate: undefined,
    })
    navigate('/tickets')
  }

  return (
    <>
      <h2 className="my-4 text-xl">Tickets</h2>
      <div className="grid grid-cols-4 grid-rows-1 place-items-stretch gap-3 md:gap-6">
        {ticketQuery?.isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            <Card label="Open" onClick={handleOpenTickets} value={ticketData?.totalOpenTickets || 0} />
            <Card label="New" onClick={handleNewTickets} value={ticketData?.totalNewTickets || 0} />
            <Card
              label="Needs Action"
              onClick={handleNeedsActionTickets}
              value={ticketData?.totalNeedsActionTickets || 0}
            />
            <Card
              label="Awaiting Response"
              onClick={handleAwaitingResponseTickets}
              value={ticketData?.totalAwaitingResponseTickets || 0}
            />
            <Card
              label="7-Day Guarantee"
              onClick={handleSevenDayGuaranteeTickets}
              value={ticketData?.totalSevenDayGuaranteeTickets || 0}
            />
            <Card
              label="Maintenance"
              onClick={handleMaintenanceTickets}
              value={ticketData?.totalMaintenanceTickets || 0}
            />
            <Card label="Lexit" onClick={handleLexitTickets} value={ticketData?.totalLexitTickets || 0} />
            <Card label="Escalated" onClick={handleEscalatedTickets} value={ticketData?.totalEscalatedTickets || 0} />
          </>
        )}
      </div>
    </>
  )
}

export default Tickets
